import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { apiError } from '../Documents/format';
import DocumentCardDialog from '../Documents/DocumentCardDialog';
import MarkingCodesExportDialog from './components/MarkingCodesExportDialog';
import MarkingCodesTable from './components/MarkingCodesTable';
import MarkingCodesToolbar from './components/MarkingCodesToolbar';
import { hasAnyFilter } from './constants';
import { useAdminMarkingCodes } from './hooks/useAdminMarkingCodes';
import { isCancelled, markingApi } from './markingApi';
import { MarkingMeta } from './types';

interface MetaError { text: string; forbidden: boolean }

/** «КМ'ы»: реестр кодов маркировки из накладных с фильтрами, карточкой документа и выгрузкой (один файл либо ZIP) */
const AdminMarkingCodes: React.FC = () => {
  const [meta, setMeta] = useState<MarkingMeta | null>(null);
  const [metaError, setMetaError] = useState<MetaError | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [openDocumentId, setOpenDocumentId] = useState<number | null>(null);

  const list = useAdminMarkingCodes(Boolean(meta));

  const loadMeta = useCallback((signal?: AbortSignal) => {
    setMetaError(null);
    return markingApi.meta(signal)
      .then(res => setMeta(res))
      .catch(err => {
        if (isCancelled(err)) return;
        setMetaError({ text: apiError(err, 'Не удалось загрузить раздел «КМ\'ы»'), forbidden: err?.response?.status === 403 });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [loadMeta]);

  const openExport = useCallback(() => setExportOpen(true), []);
  const closeExport = useCallback(() => setExportOpen(false), []);
  const closeDocument = useCallback(() => setOpenDocumentId(null), []);

  if (metaError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity={metaError.forbidden ? 'warning' : 'error'}
          action={metaError.forbidden ? undefined : <Button color="inherit" size="small" onClick={() => { loadMeta(); }}>Повторить</Button>}
        >
          {metaError.forbidden ? 'У вас нет доступа к кодам маркировки. Обратитесь к администратору.' : metaError.text}
        </Alert>
      </Box>
    );
  }

  if (!meta) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <MarkingCodesToolbar
        meta={meta} filters={list.filters} loading={list.loading}
        onChange={list.patchFilters} onReset={list.resetFilters} onRefresh={list.reload} onExport={openExport}
      />

      <MarkingCodesTable
        rows={list.rows} pagination={list.pagination} loading={list.loading} error={list.error} sort={list.sort}
        hasFilters={hasAnyFilter(list.filters)}
        onSort={list.toggleSort} onPage={list.setPage} onLimit={list.setLimit}
        onOpenDocument={setOpenDocumentId} onResetFilters={list.resetFilters} onRetry={list.reload}
      />

      {meta.access.export && (
        <MarkingCodesExportDialog open={exportOpen} meta={meta} initial={list.effective} onClose={closeExport} />
      )}

      <DocumentCardDialog id={openDocumentId} access={meta.documentAccess} onClose={closeDocument} onChanged={list.reload} />
    </Box>
  );
};

export default AdminMarkingCodes;
